import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, query, Timestamp, orderBy, deleteDoc, where } from 'firebase/firestore';
import { db, auth, updateBahanStockOnApproval, restoreBahanStock } from '../../firebase';
import './PersetujuanPermintaanBahan.css';
import { 
  Box, 
  Tabs, 
  Tab, 
  Button,
  TextField,
  Autocomplete,
  CircularProgress,
  Typography
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { id as idLocale } from 'date-fns/locale';

// Helper functions
const formatDate = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleDateString('id-ID');
  }
  return typeof timestamp === 'string' ? timestamp : 'N/A';
};

// Helper function to group requests by submission ID
const groupRequestsBySubmission = (requests) => {
  const grouped = {};
  requests.forEach(req => {
    if (!req.submissionId) {
      // Handle legacy requests without submissionId (group by userId as before)
      if (!grouped[req.userId]) {
        grouped[req.userId] = {
          namaUser: req.namaUser,
          userNim: req.userNim,
          submissionId: null,
          tanggalPermintaan: req.tanggalPermintaan,
          requests: []
        };
      }
      grouped[req.userId].requests.push(req);
    } else {
      // Group by submissionId for new requests
      if (!grouped[req.submissionId]) {
        grouped[req.submissionId] = {
          namaUser: req.namaUser,
          userNim: req.userNim,
          submissionId: req.submissionId,
          tanggalPermintaan: req.tanggalPermintaan,
          requests: []
        };
      }
      grouped[req.submissionId].requests.push(req);
    }
  });
  
  // Sort groups by date (newest first)
  const sortedGrouped = {};
  Object.keys(grouped)
    .sort((a, b) => {
      const timeA = grouped[a].tanggalPermintaan?.seconds || 0;
      const timeB = grouped[b].tanggalPermintaan?.seconds || 0;
      return timeB - timeA;
    })
    .forEach(key => {
      sortedGrouped[key] = grouped[key];
    });
    
  return sortedGrouped;
};

const generateReport = (requests, startDate, endDate) => {
  const filteredRequests = requests.filter(req => {
    if (!req.tanggalPermintaan || req.status !== 'approved') return false;
    const reqDate = req.tanggalPermintaan.toDate();
    return reqDate >= startDate && reqDate <= endDate;
  });

  let report = `Laporan Pengeluaran Bahan\nPeriode: ${startDate.toLocaleDateString('id-ID')} - ${endDate.toLocaleDateString('id-ID')}\n\n`;
  
  // Group by material
  const materialUsage = {};
  filteredRequests.forEach(req => {
    const key = req.namaBahan;
    if (!materialUsage[key]) {
      materialUsage[key] = {
        total: 0,
        satuan: req.satuan,
        details: []
      };
    }
    materialUsage[key].total += req.jumlahDiminta;
    materialUsage[key].details.push({
      tanggal: formatDate(req.tanggalPermintaan),
      jumlah: req.jumlahDiminta,
      peminta: req.namaUser,
      tujuan: req.tujuanPenggunaan
    });
  });

  // Generate report content
  Object.entries(materialUsage).forEach(([bahan, data]) => {
    report += `\nBahan: ${bahan}\nTotal Penggunaan: ${data.total} ${data.satuan}\n\nDetail Penggunaan:`;
    data.details.forEach(detail => {
      report += `\n- Tanggal: ${detail.tanggal}, Jumlah: ${detail.jumlah} ${data.satuan}, Peminta: ${detail.peminta}, Tujuan: ${detail.tujuan}`;
    });
    report += '\n';
  });

  return report;
};

const PersetujuanPermintaanBahan = () => {
  const [pendingRequests, setPendingRequests] = useState([]);
  const [historyRequests, setHistoryRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [startDate, setStartDate] = useState(new Date(new Date().setDate(1)));
  const [endDate, setEndDate] = useState(new Date());
  const [reportContent, setReportContent] = useState('');
  const [bahanList, setBahanList] = useState([]);
  const [selectedBahan, setSelectedBahan] = useState(null);
  const [loadingBahan, setLoadingBahan] = useState(false);
  const [expandedSubmission, setExpandedSubmission] = useState(null);

  // Fetch bahan list
  const fetchBahanList = async () => {
    setLoadingBahan(true);
    try {
      const bahanRef = collection(db, 'bahan');
      const querySnapshot = await getDocs(bahanRef);
      const data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setBahanList(data.sort((a, b) => a.nama.localeCompare(b.nama)));
    } catch (err) {
      console.error("Error fetching materials:", err);
      setError('Gagal memuat daftar bahan.');
    } finally {
      setLoadingBahan(false);
    }
  };

  useEffect(() => {
    fetchBahanList();
  }, []);

  // Fetch all requests
  const fetchRequests = async () => {
    setLoading(true);
    setError('');
    try {
      const requestsRef = collection(db, 'permintaan_bahan');
      
      // Build base queries
      let pendingQuery = query(
        requestsRef,
        where('status', '==', 'pending'),
        orderBy('tanggalPermintaan', 'desc')
      );

      let historyQuery = query(
        requestsRef,
        where('status', 'in', ['approved', 'rejected']),
        orderBy('tanggalPermintaan', 'desc')
      );

      // Add bahan filter if selected
      if (selectedBahan) {
        pendingQuery = query(pendingQuery, where('namaBahan', '==', selectedBahan.nama));
        historyQuery = query(historyQuery, where('namaBahan', '==', selectedBahan.nama));
      }

      // Execute queries
      const [pendingSnapshot, historySnapshot] = await Promise.all([
        getDocs(pendingQuery),
        getDocs(historyQuery)
      ]);

      setPendingRequests(pendingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setHistoryRequests(historySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (err) {
      console.error("Error fetching requests:", err);
      setError('Gagal memuat data permintaan bahan.');
    } finally {
      setLoading(false);
    }
  };

  // Handle delete request
  const handleDeleteRequest = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus permintaan ini secara permanen?')) {
      return;
    }
    setUpdatingId(id);
    setError('');
    try {
      // Find the request details
      const requestToDelete = [...pendingRequests, ...historyRequests].find(req => req.id === id);
      if (!requestToDelete) {
        throw new Error('Data permintaan tidak ditemukan.');
      }

      // If request was approved, restore the stock first
      if (requestToDelete.status === 'approved') {
        await restoreBahanStock(
          requestToDelete.namaBahan,
          requestToDelete.jumlahDiminta,
          requestToDelete.satuan
        );
      }

      // Then delete the request
      const requestRef = doc(db, 'permintaan_bahan', id);
      await deleteDoc(requestRef);
      await fetchRequests();
    } catch (err) {
      console.error(`Error deleting request ${id}:`, err);
      setError(`Gagal menghapus permintaan ${id}: ${err.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  // Handle expanding/collapsing a submission group
  const handleSubmissionExpand = (submissionId) => {
    setExpandedSubmission(expandedSubmission === submissionId ? null : submissionId);
  };
  
  // Handle approving all requests in a submission
  const handleApproveAllInSubmission = async (submissionId, requests) => {
    if (!window.confirm(`Apakah Anda yakin ingin menyetujui semua ${requests.length} permintaan dalam pengajuan ini?`)) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Process all requests in the submission
      for (const request of requests) {
        if (request.status === 'pending') {
          const requestRef = doc(db, 'permintaan_bahan', request.id);
          
          // Create approval history entry
          const approvalHistoryEntry = {
            approver: {
              id: auth.currentUser.uid,
              name: 'Admin',
              role: 'admin'
            },
            action: 'approved',
            timestamp: Timestamp.now()
          };

          // Update document with new status and append to approval history
          await updateDoc(requestRef, {
            status: 'approved',
            approvalHistory: request.approvalHistory 
              ? [...request.approvalHistory, approvalHistoryEntry]
              : [approvalHistoryEntry]
          });

          try {
            await updateBahanStockOnApproval(
              request.namaBahan,
              request.jumlahDiminta,
              request.satuan
            );
          } catch (stockError) {
            console.error(`Failed to update stock for ${request.namaBahan}:`, stockError);
            // Continue with other approvals even if stock update fails for one item
          }
        }
      }
      
      // Refresh the list
      fetchRequests();
      setSuccess(`Berhasil menyetujui semua permintaan dalam pengajuan.`);
    } catch (err) {
      console.error("Error approving all requests:", err);
      setError(`Gagal menyetujui semua permintaan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle rejecting all requests in a submission
  const handleRejectAllInSubmission = async (submissionId, requests) => {
    if (!window.confirm(`Apakah Anda yakin ingin menolak semua ${requests.length} permintaan dalam pengajuan ini?`)) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Process all requests in the submission
      for (const request of requests) {
        if (request.status === 'pending') {
          const requestRef = doc(db, 'permintaan_bahan', request.id);
          
          // Create approval history entry
          const approvalHistoryEntry = {
            approver: {
              id: auth.currentUser.uid,
              name: 'Admin',
              role: 'admin'
            },
            action: 'rejected',
            timestamp: Timestamp.now()
          };

          // Update document with new status and append to approval history
          await updateDoc(requestRef, {
            status: 'rejected',
            approvalHistory: request.approvalHistory 
              ? [...request.approvalHistory, approvalHistoryEntry]
              : [approvalHistoryEntry]
          });
        }
      }
      
      // Refresh the list
      fetchRequests();
      setSuccess(`Berhasil menolak semua permintaan dalam pengajuan.`);
    } catch (err) {
      console.error("Error rejecting all requests:", err);
      setError(`Gagal menolak semua permintaan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle status update (Approve/Reject)
  const handleUpdateRequestStatus = async (id, newStatus, silent = false) => {
    if (!silent) setUpdatingId(id);
    setError('');
    try {
      const requestRef = doc(db, 'permintaan_bahan', id);
      const requestToUpdate = pendingRequests.find(req => req.id === id);

      if (!requestToUpdate) {
        throw new Error("Request details not found locally.");
      }

      // Create approval history entry
      const approvalHistoryEntry = {
        approver: {
          id: auth.currentUser.uid,
          name: 'Admin', // Since this is the admin page
          role: 'admin'
        },
        action: newStatus,
        timestamp: Timestamp.now()
      };

      // Update document with new status and append to approval history
      await updateDoc(requestRef, {
        status: newStatus,
        approvalHistory: requestToUpdate.approvalHistory 
          ? [...requestToUpdate.approvalHistory, approvalHistoryEntry]
          : [approvalHistoryEntry]
      });

      if (newStatus === 'approved') {
        try {
          await updateBahanStockOnApproval(
            requestToUpdate.namaBahan,
            requestToUpdate.jumlahDiminta,
            requestToUpdate.satuan
          );
        } catch (stockError) {
          if (!silent) {
            setError(`Status permintaan ${id} diperbarui, tetapi gagal mengurangi stok: ${stockError.message}`);
            setUpdatingId(null);
            return;
          }
        }
      }

      if (!silent) {
        await fetchRequests();
      }

    } catch (err) {
      console.error(`Error updating request ${id} status:`, err);
      if (!silent) {
        setError(`Gagal memperbarui status permintaan ${id}: ${err.message}`);
      }
    } finally {
      if (!silent) setUpdatingId(null);
    }
  };

  // Handle generating report
  const handleGenerateReport = () => {
    const report = generateReport(historyRequests, startDate, endDate);
    setReportContent(report);
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const renderTable = (data) => (
    <table className="persetujuan-bahan-table">
      <thead>
        <tr>
          <th>Nama Peminta</th>
          <th>Nama Bahan</th>
          <th>Jumlah Diminta</th>
          <th>Satuan</th>
          <th>Tgl Permintaan</th>
          <th>Status</th>
          <th>Aksi</th>
        </tr>
      </thead>
      <tbody>
        {data.map((req) => (
          <tr key={req.id}>
            <td>{req.namaUser || 'N/A'}</td>
            <td>{req.namaBahan || 'N/A'}</td>
            <td>{req.jumlahDiminta || 'N/A'}</td>
            <td>{req.satuan || 'N/A'}</td>
            <td>{formatDate(req.tanggalPermintaan)}</td>
            <td>
              <span className={`status-badge status-${req.status}`}>
                {req.status}
              </span>
            </td>
            <td className="action-buttons">
              {activeTab === 0 ? (
                <>
                  <button
                    onClick={() => handleUpdateRequestStatus(req.id, 'approved')}
                    className="btn btn-approve"
                    disabled={updatingId === req.id}
                  >
                    {updatingId === req.id ? '...' : 'Setujui'}
                  </button>
                  <button
                    onClick={() => handleUpdateRequestStatus(req.id, 'rejected')}
                    className="btn btn-reject"
                    disabled={updatingId === req.id}
                  >
                    {updatingId === req.id ? '...' : 'Tolak'}
                  </button>
                </>
              ) : (
                <button
                  onClick={() => handleDeleteRequest(req.id)}
                  className="btn btn-delete"
                  disabled={updatingId === req.id}
                >
                  {updatingId === req.id ? '...' : 'Hapus'}
                </button>
              )}
            </td>
          </tr>
        ))}
        {data.length === 0 && !loading && (
          <tr>
            <td colSpan="7" style={{ textAlign: 'center' }}>
              {activeTab === 0 ? 'Tidak ada permintaan bahan yang menunggu persetujuan.' : 'Tidak ada riwayat permintaan bahan.'}
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div className="persetujuan-bahan-container">
      <h2>Persetujuan Permintaan Bahan</h2>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      {/* Search Section */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ maxWidth: 400, mb: 2 }}>
          <Autocomplete
            options={bahanList}
            getOptionLabel={(option) => 
              `${option.nama}${option.kode ? ` - ${option.kode}` : ''}`
            }
            onChange={(event, newValue) => {
              setSelectedBahan(newValue);
              fetchRequests();
            }}
            value={selectedBahan}
            loading={loadingBahan}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter berdasarkan bahan"
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loadingBahan && <CircularProgress color="inherit" size={20} />}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body1">
                    {option.nama}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {option.kode && `Kode: ${option.kode}`}
                    {option.jenis && ` | Jenis: ${option.jenis}`}
                  </Typography>
                </Box>
              </Box>
            )}
          />
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label="Permintaan Pending" />
            <Tab label="Riwayat" />
            <Tab label="Laporan" />
          </Tabs>
        </Box>
      </Box>

      {loading ? (
        <div className="loading-message">Memuat data permintaan...</div>
      ) : (
        <div className="persetujuan-bahan-content">
          {activeTab === 0 && (
            <div className="persetujuan-bahan-container">
              {Object.entries(groupRequestsBySubmission(pendingRequests)).map(([submissionId, submissionData]) => (
                <div key={submissionId} className="submission-group">
                  <div 
                    className="submission-header"
                    onClick={() => handleSubmissionExpand(submissionId)}
                  >
                    <div className="submission-info">
                      <h3>{submissionData.namaUser} ({submissionData.userNim || 'NIM tidak tersedia'})</h3>
                      <p>{submissionData.requests.length} permintaan - {formatDate(submissionData.tanggalPermintaan)}</p>
                    </div>
                    
                    {/* Batch approval buttons */}
                    {submissionData.submissionId && submissionData.requests.some(req => req.status === 'pending') && (
                      <div className="batch-actions" onClick={(e) => e.stopPropagation()}>
                        <button
                          className="btn btn-approve"
                          onClick={() => handleApproveAllInSubmission(submissionId, submissionData.requests)}
                          disabled={loading}
                        >
                          Setujui Semua
                        </button>
                        <button
                          className="btn btn-reject"
                          onClick={() => handleRejectAllInSubmission(submissionId, submissionData.requests)}
                          disabled={loading}
                        >
                          Tolak Semua
                        </button>
                      </div>
                    )}
                    
                    <span className="expand-icon">
                      {expandedSubmission === submissionId ? '▲' : '▼'}
                    </span>
                  </div>
                  
                  {expandedSubmission === submissionId && (
                    <div className="persetujuan-bahan-table-container">
                      <table className="persetujuan-bahan-table">
                        <thead>
                          <tr>
                            <th>Nama Bahan</th>
                            <th>Jumlah Diminta</th>
                            <th>Satuan</th>
                            <th>Tgl Permintaan</th>
                            <th>Status</th>
                            <th>Aksi</th>
                          </tr>
                        </thead>
                        <tbody>
                          {submissionData.requests.map((req) => (
                            <tr key={req.id}>
                              <td>{req.namaBahan || 'N/A'}</td>
                              <td>{req.jumlahDiminta || 'N/A'}</td>
                              <td>{req.satuan || 'N/A'}</td>
                              <td>{formatDate(req.tanggalPermintaan)}</td>
                              <td>
                                <span className={`status-badge status-${req.status}`}>
                                  {req.status}
                                </span>
                              </td>
                              <td className="action-buttons">
                                {req.status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => handleUpdateRequestStatus(req.id, 'approved')}
                                      className="btn btn-approve"
                                      disabled={updatingId === req.id}
                                    >
                                      {updatingId === req.id ? '...' : 'Setujui'}
                                    </button>
                                    <button
                                      onClick={() => handleUpdateRequestStatus(req.id, 'rejected')}
                                      className="btn btn-reject"
                                      disabled={updatingId === req.id}
                                    >
                                      {updatingId === req.id ? '...' : 'Tolak'}
                                    </button>
                                  </>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
              
              {pendingRequests.length === 0 && !loading && (
                <div className="empty-message">
                  Tidak ada permintaan bahan yang menunggu persetujuan.
                </div>
              )}
            </div>
          )}
          
          {activeTab === 1 && (
            <div className="persetujuan-bahan-table-container">
              {renderTable(historyRequests)}
            </div>
          )}

          {activeTab === 2 && (
            <Box sx={{ p: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={idLocale}>
                <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                  <DatePicker
                    label="Tanggal Mulai"
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    slotProps={{ textField: { variant: 'outlined' } }}
                  />
                  <DatePicker
                    label="Tanggal Selesai"
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    slotProps={{ textField: { variant: 'outlined' } }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleGenerateReport}
                    sx={{ ml: 2 }}
                  >
                    Generate Laporan
                  </Button>
                </Box>
              </LocalizationProvider>

              {reportContent && (
                <Box sx={{ mt: 2 }}>
                  <pre style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                    {reportContent}
                  </pre>
                </Box>
              )}
            </Box>
          )}
        </div>
      )}
    </div>
  );
};

export default PersetujuanPermintaanBahan;
